import React from "react";
import ISO6391 from "iso-639-1";
import {
  ExternalLinkIcon,
  LogoutIcon,
  MailIcon,
} from "@heroicons/react/outline";

import UserService from "../../services/UserService";
import LanguageService from "../../services/LanguageService";
import EasyTrans from "../../components/bundel/EasyTrans";
import { Button, Card, PageContainer } from "../../components/ui";

function initials(name, username) {
  const source = (name || username || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Row({ label, children }) {
  const empty = children === undefined || children === null || children === "";
  return (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 break-words text-sm text-gray-900 sm:col-span-2 sm:mt-0">
        {empty ? "—" : children}
      </dd>
    </div>
  );
}

const Profil = () => {
  const displayName = UserService.getDisplayName();
  const username = UserService.getUsername();
  const email = UserService.getEMail();
  const firstName = UserService.getFirstName();
  const lastName = UserService.getLastName();
  const userId = UserService.getUserId();
  const roles = (UserService.getRoleAccessList?.() || []).filter(
    (r) => !["offline_access", "uma_authorization", "default-roles"].some((x) =>
      r?.startsWith(x),
    ),
  );

  return (
    <PageContainer>
      {/* identity header */}
      <Card>
        <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:text-left">
          <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-fairdarkgreen to-fairbrightgreen text-2xl font-bold text-white shadow-sm">
            {initials(displayName, username)}
          </div>
          <div className="min-w-0 text-center sm:text-left">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              {displayName || username}
            </h1>
            {username && (
              <p className="text-sm text-gray-500">@{username}</p>
            )}
            {email && (
              <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-gray-500 sm:justify-start">
                <MailIcon className="h-4 w-4" aria-hidden="true" />
                {email}
              </p>
            )}
            {roles.length > 0 && (
              <div className="mt-3 flex flex-wrap justify-center gap-1.5 sm:justify-start">
                {roles.map((r) => (
                  <span
                    key={r}
                    className="inline-flex items-center rounded-full bg-fairblue-50 px-2.5 py-0.5 text-xs font-medium text-fairblue-700"
                  >
                    {r}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* account details */}
      <Card className="mt-6">
        <h2 className="text-base font-semibold text-gray-900">
          <EasyTrans>Settings.Profil.AccountDetails</EasyTrans>
        </h2>
        <dl className="mt-2 divide-y divide-gray-100">
          <Row label={<EasyTrans>Settings.Profil.FirstName</EasyTrans>}>
            {firstName}
          </Row>
          <Row label={<EasyTrans>Settings.Profil.LastName</EasyTrans>}>
            {lastName}
          </Row>
          <Row label={<EasyTrans>Settings.Profil.EMail</EasyTrans>}>
            {email}
          </Row>
          <Row label={<EasyTrans>Settings.Profil.Username</EasyTrans>}>
            {username}
          </Row>
          <Row label={<EasyTrans>Settings.Profil.UserId</EasyTrans>}>
            {userId && (
              <span className="font-mono text-xs text-gray-600">{userId}</span>
            )}
          </Row>
        </dl>
      </Card>

      {/* preferences */}
      <Card className="mt-6">
        <h2 className="text-base font-semibold text-gray-900">
          <EasyTrans>Settings.Profil.Preferences</EasyTrans>
        </h2>
        <div className="mt-3 max-w-xs">
          <label
            htmlFor="select-language"
            className="block text-sm font-medium text-gray-700"
          >
            <EasyTrans>Language</EasyTrans>
          </label>
          <select
            id="select-language"
            name="select-language"
            defaultValue={LanguageService.getLanguage()}
            onChange={(event) =>
              LanguageService.changeLanguage(event.target.value)
            }
            className="mt-1 block w-full rounded-lg border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-fairblue-500 focus:outline-none focus:ring-2 focus:ring-fairblue-500 sm:text-sm"
          >
            {ISO6391.getLanguages(["en", "de"]).map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.nativeName}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {/* account management */}
      <Card className="mt-6">
        <h2 className="text-base font-semibold text-gray-900">
          <EasyTrans>Settings.Profil.ManageTitle</EasyTrans>
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          <EasyTrans>Settings.Profil.ManageDesc</EasyTrans>
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <a
            href="https://id.fairkom.net/auth/realms/fairlogin/account"
            target="_blank"
            rel="noreferrer"
            className="sm:w-auto"
          >
            <Button variant="primary" fullWidth className="sm:w-auto">
              <span className="inline-flex items-center gap-2">
                <ExternalLinkIcon className="h-5 w-5" aria-hidden="true" />
                <EasyTrans>Settings.Profil.ManageButton</EasyTrans>
              </span>
            </Button>
          </a>
          <Button
            variant="outline"
            fullWidth
            className="sm:w-auto"
            onClick={() => UserService.doLogout()}
          >
            <span className="inline-flex items-center gap-2">
              <LogoutIcon className="h-5 w-5" aria-hidden="true" />
              <EasyTrans>Settings.Profil.Logout</EasyTrans>
            </span>
          </Button>
        </div>
      </Card>
    </PageContainer>
  );
};

export default Profil;
